import { Link } from 'react-router-dom';
import { useSourceContactsIndex } from '@/client/api/marketing/crew';
import type { SourceKind } from '@/client/types/marketing/sources';

/**
 * Contact chips for one source row — an account or an outlet.
 *
 * Reads from a shared reverse index (`useSourceContactsIndex`) so a 50-row
 * Accounts table costs one collaborators fetch, not fifty contact round trips.
 * Detail drawers that need a fresh reverse lookup still use
 * `useSourceContactsQuery`.
 *
 * A failed lookup renders nothing rather than an error. This is a supporting
 * column; the account itself loaded fine, and putting a warning in every row of
 * a fifty-row table would bury the one thing that did break.
 */
export function SourceContactsCell({
  sourceId,
  sourceKind = 'account',
  fallback = [],
}: {
  /** Shown when no person is linked, e.g. an outlet's preferred email/phone. */
  fallback?: string[];
  /** `said` for an account, `moid` for an outlet — matching `sourceKind`. */
  sourceId: number;
  sourceKind?: SourceKind;
}) {
  const { contactsFor, isLoading, isError } = useSourceContactsIndex();

  if (isLoading) return <span className="text-xs text-gray-300">…</span>;
  if (isError) return <span className="text-xs text-gray-300">—</span>;

  const contacts = contactsFor(sourceKind, sourceId);
  if (contacts.length === 0 && fallback.length > 0) {
    return (
      <div className="space-y-0.5">
        {fallback.map((v) => (
          <div key={v} className="max-w-[180px] truncate text-xs text-gray-700" title={v}>
            {v}
          </div>
        ))}
      </div>
    );
  }
  if (contacts.length === 0) {
    return (
      <Link to="/admin/marketing/crew/contacts" className="text-xs text-blue-700 hover:underline">
        Add contact
      </Link>
    );
  }

  return (
    <div className="space-y-0.5">
      {contacts.slice(0, 2).map((c) => (
        <div key={c.mcsid} className="text-xs text-gray-700">
          {c.name}
          {c.is_account_holder ? (
            <span className="ml-1 text-[10px] text-sky-700">holder</span>
          ) : null}
        </div>
      ))}
      {contacts.length > 2 ? (
        <div className="text-[10px] text-gray-400">+{contacts.length - 2} more</div>
      ) : null}
    </div>
  );
}
